import { onDestroy } from 'svelte';
import { refreshBus } from '$lib/stores/refresh.svelte';

/**
 * Store WS générique : se connecte, expose la dernière donnée reçue en
 * réactif, se reconnecte avec un backoff exponentiel (plafonné) si la
 * connexion tombe — un onglet laissé ouvert toute la nuit doit se rebrancher
 * tout seul. Se ferme automatiquement à la destruction du composant appelant
 * (onDestroy) : sans ça, chaque navigation vers une page WS laissait la
 * connexion précédente ouverte indéfiniment côté client ET serveur.
 */
export function createWsStore<T>(path: string) {
	let data = $state<T | null>(null);
	let connected = $state(false);
	let socket: WebSocket | null = null;
	let retry = 0;
	let closedByUser = false;

	function connect() {
		const proto = location.protocol === 'https:' ? 'wss' : 'ws';
		socket = new WebSocket(`${proto}://${location.host}${path}`);
		socket.onopen = () => {
			connected = true;
			retry = 0;
		};
		socket.onmessage = (ev) => {
			try {
				data = JSON.parse(ev.data) as T;
			} catch {
				// message non-JSON ignoré
			}
		};
		socket.onclose = () => {
			connected = false;
			if (closedByUser) return;
			const delay = Math.min(1000 * 2 ** retry, 15000);
			retry += 1;
			setTimeout(connect, delay);
		};
		socket.onerror = () => socket?.close();
	}

	connect();

	// Bouton "Rafraîchir" global (Sidebar, à côté de l'indicateur de
	// connexion) : force une reconnexion immédiate plutôt que d'attendre le
	// prochain tick serveur. Une reconnexion WS renvoie toujours un
	// instantané complet et à jour au handshake (voir ws_overview et
	// consorts côté backend) -- fermer/rouvrir EST donc le refetch le plus
	// simple ici, sans dupliquer une route REST par vue. Le premier passage
	// de cet effect (montage) ne doit rien fermer, seulement s'abonner.
	let firstRun = true;
	$effect(() => {
		refreshBus.tick;
		if (firstRun) {
			firstRun = false;
			return;
		}
		socket?.close(); // onclose planifie déjà une reconnexion (retry est à 0 juste après un onopen réussi -> délai ~1s)
	});

	onDestroy(() => {
		closedByUser = true;
		socket?.close();
	});

	return {
		get data() {
			return data;
		},
		get connected() {
			return connected;
		},
		close() {
			closedByUser = true;
			socket?.close();
		}
	};
}
